// Application Settings
// -----------------------

import { useState, useCallback } from "react";

import {
  BUTTON_NEXT,
  BUTTON_PREV,
  EMPTY_STRING,
  MAX_LENGTH_LINK_SCOPE,
  MAX_LENGTH_SECURITY_HEADER_NAME,
  MAX_LENGTH_SECURITY_HEADER_VALUE,
  REQUEST_RATE_TIERS,
  resolveRequestRateTierValue,
} from "@/constants/definitions";
import { setSecurityHeaderForWebapp } from "@/services/dataListOperations.service";
import { Gauge, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import PathScopeExamples from "@/components/common/PathScopeExamples";
import SortSelect from "@/components/common/SortSelect";

const ALLOWED_PATHS_NAME = "allowedLinks";
const DENIED_PATHS_NAME = "deniedLinks";

// Character whitelist for the allowed/denied path-scope fields. Entries are
// partial URL paths (e.g. "/admin", "api/v1_beta"); ",;:" and whitespace are
// entry separators. The per-entry path grammar is mirrored authoritatively
// on the backend (models/text_sanitization.py _SCOPE_PATH_ALLOWED). See
// SECURITY_AUDIT_FINDINGS F8.
const URL_WHITELIST_CHARS = /^[\sA-Za-z0-9,;:._~/%*-]+$/;
// Split on whitespace, comma, semi colon, colon
const ARRAY_SPLIT_CHARS = /[\s,;:]+/;

const ERROR_INVALID_CHARS_IN_ALLOWED_LIST =
  "The exceptions list can only contain letters, numbers, and / . - : ; separate multiple with commas.";
const ERROR_INVALID_CHARS_IN_DENIED_LIST =
  "The denied paths list can only contain letters, numbers, and / . - : ; separate multiple with commas.";

interface ApplicationSettingsProps {
  webapp: {
    id: string;
    allowedLinks: string[];
    deniedLinks: string[];
    maxRequestsPerSecond: number;
    securityHeaderName: string;
    securityHeaderValueSet: boolean;
    updateAppSettings: () => void;
  };
  onComplete: (name: string, webapp: any, securityHeaderValue: string) => void;
  // Add-Application wizard only. Presence of onCancel also distinguishes
  // wizard mode from the existing-app Settings flow — in wizard mode the
  // primary button reads "Add application" and visually outranks
  // Previous; in Settings mode the button is the plain "Next" /
  // continuation affordance.
  onCancel?: () => void;
  // Wizard-only: the BE error message from the last Create attempt,
  // if any. Surfaced above the button row so the user can see why
  // the create failed instead of being bounced silently back to
  // this step.
  createError?: string | null;
}

function ApplicationSettings({ webapp, onComplete, onCancel, createError }: ApplicationSettingsProps) {
  // In the Add-Application wizard, this step's primary action is the
  // commit moment — the BE createWebApplication mutation fires when
  // the user clicks the button. Label + styling make that explicit.
  // Outside the wizard (existing-app Settings flow) the same button
  // is just "advance the step", so the plain "Next" copy applies.
  const isWizardCreateStep = !!onCancel;
  const [allowedLinks, setAllowedLinks] = useState<string>(
    webapp.allowedLinks ? webapp.allowedLinks.join(", ") : EMPTY_STRING
  );
  const [deniedLinks, setDeniedLinks] = useState<string>(
    webapp.deniedLinks ? webapp.deniedLinks.join(", ") : EMPTY_STRING
  );
  const [requestspersecond, setRequestspersecond] = useState<number>(
    resolveRequestRateTierValue(webapp.maxRequestsPerSecond)
  );
  const [errorMessage, setErrorMessage] = useState<string>(EMPTY_STRING);
  const [securityHeaderName, setSecurityHeaderName] = useState<string>(
    webapp?.securityHeaderName || ""
  );
  const [securityHeaderValue, setSecurityHeaderValue] = useState<string>("");
  const [securityHeaderValueSet, setSecurityHeaderValueSet] = useState<boolean>(
    webapp?.securityHeaderValueSet || false
  );
  const [showSecurityHeaderInput, setShowSecurityHeaderInput] = useState<boolean>(false);

  // Validate Chars
  // ---------------
  //
  const validateCharSet = useCallback((strToValidate: string, errMsg: string): void => {
    if (!URL_WHITELIST_CHARS.test(strToValidate)) {
      setErrorMessage(errMsg);
    } else {
      setErrorMessage(EMPTY_STRING);
    }
  }, []);

  // On Input Change
  // -----------------
  // Update state
  //
  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    // Set the state, even if bad chars are present, so they can see the chars
    if (name === ALLOWED_PATHS_NAME) {
      setAllowedLinks(value);
      validateCharSet(value, ERROR_INVALID_CHARS_IN_ALLOWED_LIST);
    } else if (name === DENIED_PATHS_NAME) {
      setDeniedLinks(value);
      validateCharSet(value, ERROR_INVALID_CHARS_IN_DENIED_LIST);
    }
  }, [validateCharSet]);

  // Build input lists
  // ------------------
  //
  const buildLists = useCallback((linksList: string): string[] => {
    return linksList.split(ARRAY_SPLIT_CHARS);
  }, []);

  // On Button Click
  // ----------------
  // Updates the local webapp object and passes control back to the wizard.
  // No backend writes happen here — all writes are deferred to the Finish step
  // in NewApplication so they run after createNewWebApp resolves and an ID exists.
  const onButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    const target = event.target as HTMLButtonElement;
    const name = target.name;

    const webappCopy = webapp;

    if (EMPTY_STRING === errorMessage) {
      webappCopy.allowedLinks = buildLists(allowedLinks);
      webappCopy.deniedLinks = buildLists(deniedLinks);
      webappCopy.maxRequestsPerSecond = requestspersecond;
      webappCopy.securityHeaderName = securityHeaderName;
    }

    onComplete(name, webappCopy, securityHeaderValue);
  }, [webapp, errorMessage, allowedLinks, deniedLinks, requestspersecond, securityHeaderName, securityHeaderValue, onComplete, buildLists]);

  // Save Security Header
  // ----------------------
  //
  const handleSaveSecurityHeader = useCallback(async (): Promise<void> => {
    const webappId = webapp?.id;
    if (!webappId) return;

    try {
      const result = await setSecurityHeaderForWebapp(
        webappId,
        securityHeaderName || null,
        securityHeaderValue || null
      );
      if (result) {
        setSecurityHeaderValueSet(result.securityHeaderValueSet);
        setSecurityHeaderValue("");
        setShowSecurityHeaderInput(false);
      }
    } catch (securityHeaderError) {
      console.error("Failed to save security header:", securityHeaderError);
    }
  }, [webapp?.id, securityHeaderName, securityHeaderValue]);

  // Clear Security Header
  // ----------------------
  //
  const handleClearSecurityHeader = useCallback((): void => {
    const webappId = webapp?.id;
    if (!webappId) return;

    setSecurityHeaderForWebapp(webappId, null, null)
      .then((result) => {
        if (result) {
          setSecurityHeaderName("");
          setSecurityHeaderValue("");
          setSecurityHeaderValueSet(false);
          setShowSecurityHeaderInput(false);
        }
      })
      .catch((securityHeaderError) =>
        console.error("Failed to clear security header:", securityHeaderError)
      );
  }, [webapp?.id]);

  return (
    <div className="flex flex-col gap-8">
      {errorMessage && (
        <p className="text-sm text-red-600">{errorMessage}</p>
      )}

      {/* Request Rate */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Request Rate</h3>
        <p className="text-xs text-gray-400 mb-4">
          How hard we drive your application during testing. Lower rates are gentler and
          less likely to trigger rate limits or alerts; higher rates complete tests faster.
          We automatically ease off if your application shows signs of strain. Start low if
          you are unsure.
        </p>
        <FormField label="Request rate" htmlFor="requestspersecond" className="w-32">
          <SortSelect
            id="requestspersecond"
            value={String(requestspersecond)}
            onChange={(next) => setRequestspersecond(parseInt(next, 10))}
            options={REQUEST_RATE_TIERS.map((tier) => ({
              value: String(tier.requestsPerSecond),
              label: tier.label,
            }))}
            ariaLabel="Request rate"
            icon={Gauge}
          />
        </FormField>
      </section>

      <div className="border-t border-gray-100" />

      {/* Path Scope */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Path Scope</h3>
        <p className="text-xs text-gray-400 mb-4">
          Controls which parts of your application are tested. If left unset, we test
          everything in scope. Use denied paths to exclude areas, and exceptions to carve
          out parts of a denied area that should still be tested.
        </p>
        <PathScopeExamples />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Denied paths"
            htmlFor="deniedLinks"
            helperText="Pages and API endpoints containing this path segment will be excluded from testing."
          >
            <Input
              type="text"
              name={DENIED_PATHS_NAME}
              id="deniedLinks"
              placeholder="e.g. admin, delete-account"
              value={deniedLinks}
              maxLength={MAX_LENGTH_LINK_SCOPE}
              onChange={onInputChange}
            />
          </FormField>
          <FormField
            label="Exceptions"
            htmlFor="allowedLinks"
            helperText="Exceptions / carve-outs to denied paths."
          >
            <Input
              type="text"
              name={ALLOWED_PATHS_NAME}
              id="allowedLinks"
              placeholder="e.g. admin/dashboard"
              value={allowedLinks}
              maxLength={MAX_LENGTH_LINK_SCOPE}
              onChange={onInputChange}
            />
          </FormField>
        </div>
      </section>

      <div className="border-t border-gray-100" />

      {/* Security Header */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Security Header</h3>
        <p className="text-xs text-gray-400 mb-4">
          An optional HTTP header sent with every request during testing. Your application
          can use this to identify test traffic and allow it through WAFs, rate limiters,
          or alert rules without triggering false positives.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Header name" htmlFor="securityHeaderName">
            <Input
              type="text"
              name="securityHeaderName"
              id="securityHeaderName"
              placeholder="e.g. X-NIMIS-Token"
              value={securityHeaderName}
              maxLength={MAX_LENGTH_SECURITY_HEADER_NAME}
              onChange={(headerNameEvent) =>
                setSecurityHeaderName(headerNameEvent.target.value)
              }
            />
          </FormField>
          <FormField label="Header value" htmlFor="securityHeaderValue">
            {securityHeaderValueSet && !showSecurityHeaderInput ? (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-sm text-gray-400">Configured</span>
                <button
                  type="button"
                  onClick={() => setShowSecurityHeaderInput(true)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                >
                  Change
                </button>
                <button
                  type="button"
                  onClick={handleClearSecurityHeader}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 cursor-pointer"
                >
                  Clear
                </button>
              </div>
            ) : (
              <Input
                type="password"
                name="securityHeaderValue"
                id="securityHeaderValue"
                placeholder="Encrypted in storage"
                value={securityHeaderValue}
                maxLength={MAX_LENGTH_SECURITY_HEADER_VALUE}
                onChange={(headerValueEvent) =>
                  setSecurityHeaderValue(headerValueEvent.target.value)
                }
              />
            )}
          </FormField>
        </div>
      </section>

      {createError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {createError}
        </div>
      )}

      <div className={`flex items-center pt-4 border-t border-gray-100 ${onCancel ? "justify-between" : "justify-end"}`}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            name={BUTTON_PREV}
            onClick={onButtonClick}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Previous
          </button>
          {isWizardCreateStep ? (
            // Distinct from a plain Next: prefixed with a Plus icon
            // and styled larger (px-5, gap-2, shadow) so the user
            // reads it as the commit action, not just "advance step".
            <button
              type="button"
              name={BUTTON_NEXT}
              onClick={onButtonClick}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              Add application
            </button>
          ) : (
            <button
              type="button"
              name={BUTTON_NEXT}
              onClick={onButtonClick}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApplicationSettings;
